/**
 * WAF custom Origin Rules (Owl/Mlytics). Service-scoped (path: `/services/:service_id/`) and
 * domain-anchored (`?domain=`). Rules are an ordered list (by priority) of match
 * `conditions` + a single `proxyPass` action that re-routes matched requests to a
 * chosen origin.
 *
 * Backed by `modules/service/waf/controller_origin_rules.go`:
 *   - GET    /services/:service_id/waf/origin/schema          → rule-builder vocabulary (fields/operators/actions)
 *   - GET    /services/:service_id/waf/origin/rules           → a domain's rules (sorted by priority)
 *   - POST   /services/:service_id/waf/origin/rules           → create a rule
 *   - PATCH  /services/:service_id/waf/origin/rules/:id        → replace a rule (full body)
 *   - PATCH  /services/:service_id/waf/origin/rules/:id/enable → toggle a rule on/off
 *   - DELETE /services/:service_id/waf/origin/rules/:id        → delete a rule
 *
 * The condition shape is identical to firewall rules; only the action differs
 * (proxy-pass origin config instead of block/challenge/…).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/* ----------------------------- Rule-builder schema ----------------------------- */

/** One selectable value for an action field (vendor mixes string/number values). */
export interface OriginFieldOption {
  label?: string;
  value: string;
}

/** One comparison a field supports. `options` carries allowed values (methods, geo). */
export interface OriginOperator {
  value: string;       // streq | contains | pm | geo_pm | isIn ...
  label: string;
  type: string;        // string | number | geo
  example?: string;
  options?: string[];
  input_value: boolean; // needs a value
}

/** One matchable request attribute. `input_value` = the field itself takes a key. */
export interface OriginField {
  value: string;       // e.g. "ARGS:match"
  label: string;
  type: string;
  example?: string;
  description?: string;
  input_value: boolean;
  operators: OriginOperator[];
}

/** One configurable input of an action. */
export interface OriginActionField {
  field: string;       // originType | sources | port | sslPolicies | sni ...
  label: string;
  type: string;        // string | number | boolean
  unit?: string;
  example?: string;
  range?: number[];
  input_value?: boolean;
  options?: OriginFieldOption[];
}

/** One rule action (currently only `proxyPass`). */
export interface OriginActionSchema {
  value: string;
  label: string;
  type: string;
  input_value: boolean;
  additional_fields?: OriginActionField[];
}

/** Rule-builder catalog: matchable fields + available actions. */
export interface OriginRuleSchema {
  fields: OriginField[];
  actions: OriginActionSchema[];
}

/** Fields/operators/actions vocabulary for building custom origin rules. */
export function useWafOriginRuleSchema(serviceId: string | undefined) {
  return useQuery<OriginRuleSchema>({
    queryKey: wafKeys.originSchema(serviceId ?? ''),
    enabled: !!serviceId,
    staleTime: 30 * 60_000,
    queryFn: async () =>
      unwrap<OriginRuleSchema>(
        await serviceApiV3.get(`/services/${serviceId}/waf/origin/schema`),
      ) ?? { fields: [], actions: [] },
  });
}

/** The `proxyPass` action — where matched requests are proxied. */
export interface OriginRuleAction {
  type: string;            // "proxyPass"
  origin_type?: string;    // "FQDN" | "IPv4"
  sources?: string[];      // origin IPs/hosts
  ssl_policies?: string[]; // any of "Match View" | "HTTP" | "HTTPS"
  port_http?: number;      // HTTP port [1,65535]
  port_https?: number;     // HTTPS port [1,65535]
  balance_mode?: string;   // "consistent" | "roundrobin"
  sni?: boolean;           // enable SNI
  ups_host?: string;       // custom host header
}

/** One match condition. `logic` ("and"|"or") connects to the NEXT condition. */
export interface OriginRuleCondition {
  field: string;
  key?: string;
  operator: string;
  value?: string;
  values?: string[];
  logic?: string;
}

/** A stored custom origin rule (`GET /waf/origin/rules`, sorted by priority). */
export interface OriginRule {
  id: string;
  rule_id: number;
  name: string;
  enable: boolean;
  priority: number;
  type: string;
  action: OriginRuleAction;
  conditions: OriginRuleCondition[];
  created_at?: number;
  updated_at?: number;
}

/** Create/update body (full replace on update). 1–5 conditions. */
export interface OriginRuleInput {
  name: string;
  enable?: boolean;
  action: OriginRuleAction;
  conditions: OriginRuleCondition[];
}

/** A domain's custom origin rules (sorted by priority). */
export function useWafOriginRules(serviceId: string | undefined, domain: string | undefined) {
  return useQuery<OriginRule[]>({
    queryKey: wafKeys.originRules(serviceId ?? '', domain ?? ''),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () =>
      unwrap<OriginRule[]>(
        await serviceApiV3.get(`/services/${serviceId}/waf/origin/rules`, { params: { domain } }),
      ) ?? [],
  });
}

function invalidateRules(qc: ReturnType<typeof useQueryClient>, serviceId?: string) {
  if (serviceId) qc.invalidateQueries({ queryKey: ['waf', serviceId, 'origin-rules'] });
}

/** Create an origin rule (`POST /waf/origin/rules`). */
export function useCreateOriginRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; input: OriginRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, input }: { domain: string; input: OriginRuleInput }) =>
      unwrap(
        await serviceApiV3.post(`/services/${serviceId}/waf/origin/rules`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Replace an origin rule (`PATCH /waf/origin/rules/:id`, full body). */
export function useUpdateOriginRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; input: OriginRuleInput }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, input }: { domain: string; id: string; input: OriginRuleInput }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/origin/rules/${id}`, input, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Toggle an origin rule on/off (`PATCH /waf/origin/rules/:id/enable`). */
export function useSetOriginRuleEnabled(
  serviceId: string | undefined,
  options?: UseMutationOptions<unknown, Error, { domain: string; id: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id, enable }: { domain: string; id: string; enable: boolean }) =>
      unwrap(
        await serviceApiV3.patch(`/services/${serviceId}/waf/origin/rules/${id}/enable`, { enable }, { params: { domain } }),
      ),
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}

/** Delete an origin rule (`DELETE /waf/origin/rules/:id`). */
export function useDeleteOriginRule(
  serviceId: string | undefined,
  options?: UseMutationOptions<void, Error, { domain: string; id: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, id }: { domain: string; id: string }) => {
      await serviceApiV3.delete(`/services/${serviceId}/waf/origin/rules/${id}`, { params: { domain } });
    },
    ...options,
    onSuccess: (...args) => { invalidateRules(qc, serviceId); options?.onSuccess?.(...args); },
  });
}
